import { useCallback, useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { CalendarClock, Pause, Play, X, Pencil, Plus } from "lucide-react";
import TareaProgramadaDialog from "@/components/persona/TareaProgramadaDialog";
import { resumenRegla, avisoTexto, slotTexto, ESTADO_TAREA, ESTADO_OCURRENCIA } from "@/lib/programados";

// v2.13.1 — Programados de la PERSONA: aquí se CONFIGURA (crear/editar/pausar/reanudar/cancelar y
// ver próximos horarios). La OPERACIÓN ocurre en Central: cuando llega la hora, el recordatorio
// genera una alerta MEDIA normal. Esta pestaña no opera alertas vencidas.
export default function ProgramadosSection({ personaId, puedeGestionar }) {
  const [tareas, setTareas] = useState([]);
  const [ocurrencias, setOcurrencias] = useState([]);
  const [dialogo, setDialogo] = useState(null); // {tarea} | {nueva:true}

  const load = useCallback(async () => {
    try {
      const [t, o] = await Promise.all([
        api.get(`/programados/tareas?persona_id=${personaId}`),
        api.get(`/programados/ocurrencias?persona_id=${personaId}&limite=40`),
      ]);
      setTareas(t.data.tareas || []);
      setOcurrencias(o.data.ocurrencias || []);
    } catch {
      toast.error("No se pudieron cargar los recordatorios.");
    }
  }, [personaId]);

  useEffect(() => { load(); }, [load]);

  const accion = async (tarea, verbo) => {
    try {
      await api.post(`/programados/tareas/${tarea.id}/${verbo}`, { motivo: "" });
      toast.success("Recordatorio actualizado.");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo actualizar.");
    }
  };

  return (
    <section className="bg-white rounded-2xl border border-gray-100 p-5 md:p-6 space-y-5" data-testid="persona-programados">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-base font-medium text-[#012472] flex items-center gap-2">
          <CalendarClock strokeWidth={1.6} className="h-4 w-4" /> Programados
        </h3>
        {puedeGestionar && (
          <Button size="sm" className="rounded-lg bg-[#012472] hover:bg-[#012472]/90 text-white"
            onClick={() => setDialogo({ nueva: true })} data-testid="programado-nuevo-btn">
            <Plus strokeWidth={1.75} className="h-4 w-4 mr-1" /> Nuevo
          </Button>
        )}
      </div>
      <p className="text-xs text-slate-400">
        Al llegar la hora, el recordatorio entra a Central como una alerta media normal: se toma, se
        llama a la persona y se cierra ahí. La consola guía; la persona decide.
      </p>

      {tareas.length === 0 ? (
        <p className="text-sm text-slate-500" data-testid="programado-sin-tareas">Sin recordatorios capturados.</p>
      ) : (
        <div className="space-y-3">
          {tareas.map((t) => (
            <div key={t.id} className="rounded-xl border border-gray-100 p-4" data-testid={`programado-tarea-${t.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-800">{t.titulo}</p>
                  <p className="text-xs text-slate-500 mt-1">{resumenRegla(t.regla)} · {avisoTexto(t.anticipacion_min)}</p>
                  <p className="text-xs text-slate-400 mt-1">{ESTADO_TAREA[t.estado] || t.estado}</p>
                </div>
                {puedeGestionar && (
                  <div className="flex items-center gap-1.5">
                    {t.estado === "activa" && (
                      <>
                        <Button variant="outline" size="sm" className="rounded-lg" onClick={() => setDialogo({ tarea: t })}
                          data-testid={`programado-editar-${t.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                        <Button variant="outline" size="sm" className="rounded-lg" onClick={() => accion(t, "pausar")}
                          data-testid={`programado-pausar-${t.id}`}><Pause className="h-3.5 w-3.5" /></Button>
                      </>
                    )}
                    {t.estado === "pausada" && (
                      <Button variant="outline" size="sm" className="rounded-lg" onClick={() => accion(t, "reanudar")}
                        data-testid={`programado-reanudar-${t.id}`}><Play className="h-3.5 w-3.5" /></Button>
                    )}
                    {(t.estado === "activa" || t.estado === "pausada") && (
                      <Button variant="outline" size="sm" className="rounded-lg text-rose-600" onClick={() => accion(t, "cancelar")}
                        data-testid={`programado-cancelar-${t.id}`}><X className="h-3.5 w-3.5" /></Button>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="border-t border-gray-100 pt-4">
        <p className="text-xs font-medium uppercase tracking-wider text-slate-400">Próximos horarios</p>
        {ocurrencias.length === 0 ? (
          <p className="text-sm text-slate-500 mt-2" data-testid="programado-sin-ocurrencias">Sin horarios programados.</p>
        ) : (
          <ul className="mt-2 space-y-1.5" data-testid="programado-ocurrencias">
            {ocurrencias.map((o) => (
              <li key={o.id} className="flex flex-wrap items-center gap-2 text-sm text-slate-700"
                data-testid={`programado-ocurrencia-${o.id}`}>
                <span className="font-mono text-xs">{slotTexto(o.slot_local)}</span>
                <span className={`rounded-full border px-2 py-0.5 text-xs ${(ESTADO_OCURRENCIA[o.estado] || {}).tono || ""}`}>
                  {(ESTADO_OCURRENCIA[o.estado] || {}).label || o.estado}
                </span>
                <span className="text-xs text-slate-500 truncate">{o.tarea?.titulo}</span>
                {o.entregada_tarde && <span className="text-xs text-amber-600">avisado tarde · {o.retraso_min} min</span>}
              </li>
            ))}
          </ul>
        )}
      </div>

      {dialogo && (
        <TareaProgramadaDialog
          open
          onOpenChange={(v) => { if (!v) setDialogo(null); }}
          personaId={personaId}
          tarea={dialogo.tarea}
          onSaved={load}
        />
      )}
    </section>
  );
}
